// @givmo/fundraisers-module — Delete a campaign email that isn't scheduled (the KCC hand edit, 2026-11-01), from a
// row's menu in the series and from the email's own page, behind a confirmation. Only a draft or a cancelled
// email is offered it (`emailDeletable`), as only those does the server delete: a scheduled email is cancelled
// first, and one that went out stays in the series as the record. The delete is the person's alone: no tool on
// the connector deletes an email (D-EMC-8).

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@givmo/design-system';
import { ConfirmDialog } from './confirm-dialog';
import { EMAILS_COPY as C } from './emails-copy';
import { emailRefusal, type EmailRow } from './emails-decode';
import { emailKeys } from './emails-queries';
import { deleteEmail } from './emails-transport';
import { useFundraisersModule } from './fundraisers-context';
import { useLivePage } from './use-live-page';

/** Whether the server deletes this email: a draft, or a cancelled one. */
export function emailDeletable(row: Pick<EmailRow, 'state'>): boolean {
  return row.state === 'draft' || row.state === 'cancelled';
}

export function useDeleteEmail(
  fundraiserId: string,
  options: { onDeleted?: () => void } = {},
): {
  isPending: boolean;
  /** Ask to delete this email: the confirmation opens. */
  start: (row: Pick<EmailRow, 'id' | 'subject'>) => void;
  /** The confirmation, for the host to draw. */
  dialog: ReactNode;
} {
  const config = useFundraisersModule();
  const orgRef = config.orgRef ?? '';
  const client = useQueryClient();
  const live = useLivePage();
  const [asking, setAsking] = useState<Pick<EmailRow, 'id' | 'subject'> | null>(null);

  const mutation = useMutation({
    mutationFn: async (emailId: string) => {
      try {
        await deleteEmail(orgRef, fundraiserId, emailId);
      } catch (error) {
        // Already gone (another tab deleted it): what the person asked for is done.
        if (emailRefusal(error).kind !== 'gone') throw error;
      }
      return emailId;
    },
    onSuccess: async (emailId) => {
      setAsking(null);
      if (live()) toast.success(C.deleted);
      options.onDeleted?.();
      client.removeQueries({ queryKey: emailKeys.email(orgRef, fundraiserId, emailId) });
      await client.invalidateQueries({ queryKey: emailKeys.series(orgRef, fundraiserId) });
    },
    onError: (error) => {
      setAsking(null);
      const refusal = emailRefusal(error);
      toast.error(refusal.kind === 'refused' ? refusal.message : C.deleteFailed);
    },
  });

  const dialog = (
    <ConfirmDialog
      open={asking !== null}
      onOpenChange={(open) => !open && !mutation.isPending && setAsking(null)}
      title={C.deleteTitle}
      body={C.deleteBody}
      confirmLabel={C.deleteConfirm}
      cancelLabel={C.deleteKeep}
      testId="fundraiser-email-delete-dialog"
      confirmTestId="fundraiser-email-delete-confirm"
      busy={mutation.isPending}
      tone="danger"
      onConfirm={() => asking !== null && mutation.mutate(asking.id)}
    />
  );

  return { isPending: mutation.isPending, start: setAsking, dialog };
}
